import moment from 'moment';
import { Injectable, inject } from '@angular/core';
import { Router, type UrlTree } from '@angular/router';
import { DriveTargetNavigationService } from '../drive/drive-target-navigation.service';
import { ProjectService } from './project.service';
import { WorkspaceService } from './workspace.service';
import { AuthService } from './auth.service';
import { EmitterService } from './emitter.service';
import { CryptoService } from './crypto.service';
import {
  ActiveWorkspaceUpdatedEvent,
  NotificationItem,
  ProjectSection,
  ProjectSectionNames,
  ProjectSubsection,
  ProjectSubsectionNames,
  WorkspaceDTO,
} from '../interfaces';
import { NotificationObject } from './notification-helpers/notification.types';
import {
  driveCommentTarget,
  driveElementTarget,
  isDriveComment,
  notificationElement,
  orgEntityIds,
  parseElementProperties,
  type NotificationElement,
  type OrgEntityIds,
} from './notification-helpers/drive-comment.utils';
import type { DriveSelectionTarget, DriveTaskTarget } from '../drive/drive-context-state.utils';
import type { TaskEntityLink } from '@mtm/task-ui-angular';

/** The page a task notification opens, before its task is added to the `?ctx=`. */
interface TaskContextPage {
  readonly route: string[];
  readonly queryParams?: Record<string, string>;
  readonly target?: DriveSelectionTarget;
}
import { EMPTY_UUID } from '../constants';
import { ElementPathNode, normalizeElementPath } from '../utils/element-path.utils';

@Injectable()
export class ClickableNotificationService {
  private readonly driveTargetNavigation: DriveTargetNavigationService = inject(DriveTargetNavigationService);

  constructor(
    private readonly router: Router,
    private readonly authService: AuthService,
    private readonly projectService: ProjectService,
    private readonly workspaceService: WorkspaceService,
    private readonly cryptoService: CryptoService
  ) {}

  private goToProposalQaComment(projectSections: ProjectSection[], notification: NotificationItem): void {
    const projectId: string = notification?.projectId || notification?.object?.projectId;
    const section: ProjectSection | undefined = projectSections.find(
      (section: ProjectSection) => section.section === ProjectSectionNames.PRODUCTION_BRIEF
    );
    const subSection: ProjectSubsection | undefined = (section as any)?.subsections?.find(
      (subsection: ProjectSubsection) => subsection.subSection === ProjectSubsectionNames.QA
    );
    if (section && subSection) {
      this.router.navigate(['projects', projectId, section.id, subSection.id, 'preview']);
    }
  }

  private goToInvitationFirstAccepted(projectSections: ProjectSection[], notification: NotificationItem): void {
    const projectId: string = notification?.projectId || notification?.object?.projectId;
    const section: ProjectSection | undefined = projectSections.find(
      (section: ProjectSection) => section.section === ProjectSectionNames.PRODUCTION_BRIEF
    );
    const subSection: ProjectSubsection | undefined = (section as any)?.subsections?.find(
      (subsection: ProjectSubsection) => subsection.name === ProjectSubsectionNames.UPLOAD_BRIEF
    );
    if (section && subSection) {
      this.router.navigate(['projects', projectId, section.id, subSection.id, 'preview']);
    }
  }

  private goToInvitationDeclined(projectSections: ProjectSection[], notification: NotificationItem): void {
    const projectId: string = notification?.projectId || notification?.object?.projectId;
    const section: ProjectSection | undefined = projectSections.find(
      (section: ProjectSection) => section.section === ProjectSectionNames.PRODUCTION_PARTNER_SELECTION
    );
    const subSection: ProjectSubsection | undefined = (section as any)?.subsections?.find(
      (subsection: ProjectSubsection) => subsection.name === ProjectSubsectionNames.COMPANY_PROFILE_SUGGESTIONS
    );
    if (section && subSection) {
      this.router.navigate(['projects', projectId, section.id, subSection.id, 'preview']);
    }
  }

  private goToProposalDiscussion(projectSections: ProjectSection[], notification: NotificationItem): void {
    const projectId: string = notification?.projectId || notification?.object?.projectId;
    const section: ProjectSection | undefined = projectSections.find(
      (section: ProjectSection) => section.section === ProjectSectionNames.PRODUCTION_COMPANY_PROPOSALS
    );
    const subSection: ProjectSubsection | undefined = (section as any)?.subsections?.find(
      (subsection: ProjectSubsection) => subsection.name === ProjectSubsectionNames.PROPOSALS_DISCUSSION
    );
    if (section && subSection) {
      this.router.navigate(['projects', projectId, section.id, subSection.id, 'preview']);
    }
  }

  private goToProposalAccepted(notification: NotificationItem): void {
    const projectId: string = notification?.projectId || notification?.object?.projectId;
    this.router.navigate(['projects', projectId]);
  }

  private goToProjectInvitation(): void {
    this.workspaceService.setInMarketplace(true);
    this.router.navigate(['/projects']);
  }

  private goToProjectDashboard(notification: NotificationItem): void {
    const projectId: string = notification?.projectId || notification?.object?.projectId;
    this.router.navigate(['/projects', projectId]);
  }

  private goToProjectDashboardWithChatOpened(
    notification: NotificationItem,
    conversation: Record<string, string>
  ): void {
    const projectId: string = notification?.projectId || notification?.object?.projectId;
    this.router.navigate(['/projects', projectId], {
      queryParams: conversation,
    });
  }

  private goToProjectCalendar(notification: NotificationItem, noPopup?: boolean): void {
    const { projectId, eventId }: { projectId: string; eventId: string } = notification.object;
    this.router.navigate(['/projects', projectId, 'calendar'], {
      queryParams: {
        eventId: noPopup ? void 0 : eventId,
      },
    });
  }

  private parseFileVotes(fileVotes: string): string | undefined {
    try {
      const votes: Record<string, any> = JSON.parse(fileVotes);
      const [firstKey] = Object.keys(votes);
      return firstKey;
    } catch {
      return undefined;
    }
  }

  private buildSubSectionQueryParams(notification: NotificationItem): Record<string, string | number | undefined> {
    const queryParams: Record<string, string | number | undefined> = {};
    const obj: any = notification.object || {};

    if (obj.topicId) {
      queryParams.topicId = obj.topicId;
    }
    if (obj.optionId) {
      queryParams.optionId = obj.optionId;
    }
    if (obj.fileName) {
      queryParams.fileName = obj.fileName.split('/').pop();
    }
    if (obj.versionNumber) {
      queryParams.version = obj.versionNumber;
    }
    if (obj.fileId) {
      queryParams.fileId = obj.fileId;
    }
    if (obj.fileVotes) {
      const fileIdFromVotes: string | undefined = this.parseFileVotes(obj.fileVotes);
      if (fileIdFromVotes) {
        queryParams.fileId = fileIdFromVotes;
      }
    }
    if (obj.filePageNumber) {
      queryParams.page = obj.filePageNumber;
    }
    if (obj.privateConditionKey) {
      queryParams.privateConditionKey = obj.privateConditionKey;
    }
    if (obj.filePageId) {
      queryParams.pageId = obj.filePageId;
    }

    return queryParams;
  }

  private goToSubSection(projectSections: ProjectSection[], notification: NotificationItem): void {
    const data: { projectId: string; sectionId: string; subSectionId: string } = this.getProjectSectionSubSection(
      projectSections,
      notification
    );
    const { projectId, sectionId, subSectionId } = data;
    if (!projectId || !sectionId || !subSectionId) {
      return;
    }

    const queryParams: Record<string, string | number | undefined> = this.buildSubSectionQueryParams(notification);
    const obj: any = notification.object || {};

    let subRoute: string = 'preview';
    if (obj.fileId) {
      subRoute = 'preview';
    } else if (obj.topicId && !obj.optionId) {
      subRoute = 'grid';
    } else if (obj.optionId && obj.versionNumber) {
      subRoute = 'files';
    }

    this.router.navigate(['projects', projectId, sectionId, subSectionId, subRoute], {
      fragment: obj.optionId || obj.typeId || obj.itemId,
      queryParams: {
        ...queryParams,
        type: notification.category,
        notificationCode: notification.code,
        commentId: obj.commentId,
        random: moment().unix(),
      },
    });
  }

  public getProjectSectionSubSection(
    projectSections: ProjectSection[],
    notification: NotificationItem
  ): { projectId: string; sectionId: string; subSectionId: string } {
    const section: ProjectSection | undefined = projectSections.find(
      (s: ProjectSection) => s.name === notification.object?.sectionName
    );
    const subSection: ProjectSubsection | undefined = (section as any)?.subsections?.find(
      (ss: ProjectSubsection) => ss.name === notification.object?.subSectionName
    );
    return {
      projectId: notification?.projectId || notification?.object?.projectId,
      sectionId: notification?.object?.sectionId || section?.id || '',
      subSectionId: notification?.object?.subSectionId || subSection?.id || '',
    };
  }

  public goArchiveProjectPage(notification: NotificationItem): void {
    const { projectId }: { projectId: string } = notification.object;
    if (projectId) {
      this.projectService.getProjectById(projectId).subscribe((res: any) => {
        const authUser: any = this.authService.getAuthUser();
        const { workspaceId }: { workspaceId: string } = res;
        const { companyId }: { companyId: string } = authUser;
        this.workspaceService.getWorkspaceById(companyId, workspaceId).subscribe((workspace: WorkspaceDTO | null) => {
          if (!workspace) {
            return;
          }
          this.router.navigate(['projects']).then(() => {
            setTimeout(() => {
              EmitterService.get(ActiveWorkspaceUpdatedEvent).emit({
                workspace,
                inMarketplace: false,
                isArchive: true,
              });
            }, 0);
          });
        });
      });
    }
  }

  /**
   * Opens a task notification's context page — the drive element, the legacy section /
   * sub-section, or the workspace / campaign dashboard — with the task open in the task
   * sidebar. Falls back to the scoped task board when no context page resolves.
   */
  public async goToTaskPage(notification: NotificationItem): Promise<void> {
    const obj: NotificationObject = notification.object ?? {};
    const taskId: string | undefined =
      (notification as NotificationItem & { foreignId?: string }).foreignId ?? obj.taskUuid;
    const projectId: string | undefined = [notification.projectId, obj.projectId].find(
      (id: string | undefined) => !!id && id !== EMPTY_UUID
    );
    await this.openTaskInContext(obj, taskId, projectId);
  }

  /** The task board's "Related to" link: the entity's page with that task open in the task sidebar. */
  public async openTaskEntity(link: TaskEntityLink, newTab: boolean = false): Promise<void> {
    const elementType: string = link.elementType.toUpperCase().replace(/_/g, '-');
    const elementPath: NotificationObject['elementPath'] = link.elementPath as NotificationObject['elementPath'];
    const projectId: string | undefined =
      elementType === 'PROJECT' ? link.elementUuid : normalizeElementPath(elementPath)['PROJECT']?.id;
    const obj: NotificationObject = {
      taskUuid: link.taskUuid,
      boardUuid: link.boardUuid,
      parentTaskUuid: link.parentTaskUuid,
      elementType,
      elementUuid: link.elementUuid,
      elementPath,
      projectId,
    };
    // Brand-level tasks have no entity page of their own: the brand dashboard, still via `?ctx=`.
    const brandPage: TaskContextPage = { route: ['/workspaces'] };
    if (!newTab) return this.openTaskInContext(obj, link.taskUuid, projectId, brandPage);

    // Opened before any await: a tab opened after the async ctx POST can be popup-blocked.
    const tab: Window | null = window.open('', '_blank');
    try {
      const page: TaskContextPage = (await this.taskContextPage(obj, projectId)) ?? brandPage;
      const href: string = this.router.serializeUrl(await this.taskContextUrl(page, obj, link.taskUuid));
      if (tab) tab.location.href = href;
      else window.open(href, '_blank');
    } catch (error: unknown) {
      tab?.close();
      throw error;
    }
  }

  private async openTaskInContext(
    obj: NotificationObject,
    taskId: string | undefined,
    projectId: string | undefined,
    fallbackPage?: TaskContextPage
  ): Promise<void> {
    const page: TaskContextPage | null = (await this.taskContextPage(obj, projectId)) ?? fallbackPage ?? null;

    if (taskId && page) {
      await this.router.navigateByUrl(await this.taskContextUrl(page, obj, taskId));
      return;
    }

    const scopedRoute: { routePath: string[]; queryParams: Record<string, string> } | null =
      this.buildScopedTaskRoute(obj);

    if (scopedRoute && taskId) {
      scopedRoute.routePath.push(taskId);
      this.router.navigate(scopedRoute.routePath, { queryParams: scopedRoute.queryParams });
      return;
    }

    this.router.navigate(['tasks'], {
      queryParams: {
        projectId: obj.projectId,
        taskId,
      },
    });
  }

  private taskContextPage(obj: NotificationObject, projectId: string | undefined): Promise<TaskContextPage | null> {
    return projectId ? this.projectTaskPage(projectId, obj) : Promise.resolve(this.orgTaskPage(obj));
  }

  /** The page's `?ctx=` URL with the task open in the task sidebar. */
  private taskContextUrl(page: TaskContextPage, obj: NotificationObject, taskId: string): Promise<UrlTree> {
    const task: DriveTaskTarget = { taskUuid: taskId, boardUuid: obj.boardUuid, parentTaskUuid: obj.parentTaskUuid };
    return this.driveTargetNavigation.contextUrlTree(page.route, { ...page.target, task }, page.queryParams);
  }

  /** A drive project opens the element in the explorer; a legacy one its section / sub-section page. */
  private async projectTaskPage(projectId: string, obj: NotificationObject): Promise<TaskContextPage> {
    if (await this.projectService.isDriveProject(projectId)) {
      return { route: ['/projects', projectId], target: driveElementTarget(obj) };
    }
    const path: Record<string, ElementPathNode> = normalizeElementPath(obj.elementPath);
    const { type, uuid }: NotificationElement = notificationElement(obj);
    const sectionId: string | undefined = type === 'SECTION' ? uuid : path['SECTION']?.id;
    const subSectionId: string | undefined = type === 'SUB-SECTION' ? uuid : path['SUB-SECTION']?.id;
    const route: string[] = ['/projects', projectId];
    if (sectionId) route.push(sectionId, ...(subSectionId ? [subSectionId] : []));
    return { route };
  }

  /** The workspace / campaign dashboard of an org-level task (same pages `navigateToOrgEntity` opens). */
  private orgTaskPage(obj: NotificationObject): TaskContextPage | null {
    const { type, uuid }: NotificationElement = notificationElement(obj);
    const workspaceId: string | undefined = orgEntityIds(obj).workspaceId || (type === 'WORKSPACE' ? uuid : undefined);
    if (!workspaceId) return null;
    const wid: string = this.cryptoService.encryptString(workspaceId);
    if (type === 'WORKSPACE') return { route: ['/workspaces', 'campaign-drive'], queryParams: { wid } };
    if (type === 'CAMPAIGN' && uuid) {
      const dc: string = this.cryptoService.encryptString(uuid);
      return { route: ['/workspaces', 'campaigndrive', 'dashboard'], queryParams: { wid, dc } };
    }
    return null;
  }

  private buildScopedTaskRoute(
    obj: NotificationObject
  ): { routePath: string[]; queryParams: Record<string, string> } | null {
    if (!obj) {
      return null;
    }

    const path: Record<string, ElementPathNode> = normalizeElementPath(obj.elementPath);

    const props: { id?: string; lv?: number | string } = parseElementProperties(obj.elementProperties) as {
      id?: string;
      lv?: number | string;
    };
    const lv: number = Number(props.lv);
    if (!Number.isFinite(lv)) {
      return null;
    }

    const workspaceId: string = obj.workspaceId ?? path['WORKSPACE']?.id ?? '';
    const campaignId: string = path['CAMPAIGN']?.id ?? (lv === 3 ? (props.id ?? '') : '');
    const projectId: string = obj.projectId ?? path['PROJECT']?.id ?? (lv === 4 ? (props.id ?? '') : '');

    const queryParams: Record<string, string> = {};
    let routePath: string[] = [];

    switch (lv) {
      case 2: {
        if (!workspaceId) {
          return null;
        }
        const wid: string = this.cryptoService.encryptString(workspaceId);
        routePath = [`/workspaces/${wid}/tasks`];
        break;
      }
      case 3: {
        if (!workspaceId || !campaignId) {
          return null;
        }
        const wid: string = this.cryptoService.encryptString(workspaceId);
        const dc: string = this.cryptoService.encryptString(campaignId);
        routePath = [`/workspaces/${wid}/campaign-drive/${dc}/tasks`];
        break;
      }
      case 4: {
        if (!projectId) {
          return null;
        }
        routePath = [`/projects/${projectId}/tasks`];
        break;
      }
      case 5: {
        if (!projectId) {
          return null;
        }
        routePath = [`/projects/${projectId}/tasks`];
        queryParams.sectionId = props.id ?? path['SECTION']?.id ?? '';
        break;
      }
      case 6: {
        if (!projectId) {
          return null;
        }
        routePath = [`/projects/${projectId}/tasks`];
        queryParams.subsectionId = props.id ?? path['SUB-SECTION']?.id ?? '';
        break;
      }
      case 7: {
        if (!projectId) {
          return null;
        }
        routePath = [`/projects/${projectId}/tasks`];
        queryParams.folderId = props.id ?? path['FOLDER']?.id ?? '';
        break;
      }
      case 8: {
        if (!projectId) {
          return null;
        }
        routePath = [`/projects/${projectId}/tasks`];
        queryParams.subFolderId = props.id ?? path['SUB-FOLDER']?.id ?? '';
        break;
      }
      case 9: {
        if (!projectId) {
          return null;
        }
        routePath = [`/projects/${projectId}/tasks`];
        queryParams.versionId = props.id ?? path['VERSION']?.id ?? '';
        break;
      }
      case 10: {
        if (!projectId) {
          return null;
        }
        routePath = [`/projects/${projectId}/tasks`];
        queryParams.fileId = props.id ?? path['FILE']?.id ?? '';
        break;
      }
      default:
        return null;
    }

    return { routePath, queryParams };
  }

  private getNotificationHandler(
    projectSections: ProjectSection[],
    notification: NotificationItem
  ): (() => void) | null {
    const handlerMap: Record<string, () => void> = {
      PROJECT_ARCHIVED: () => this.goArchiveProjectPage(notification),
      PROJECT_USER_INVITED: () => this.goToProjectDashboard(notification),
      PROPOSAL_QA_COMMENT: () => this.goToProposalQaComment(projectSections, notification),
      INVITATION_FIRST_ACCEPTED: () => this.goToInvitationFirstAccepted(projectSections, notification),
      INVITATION_DECLINED: () => this.goToInvitationDeclined(projectSections, notification),
      PROPOSAL_UPLOADED: () => this.goToProposalDiscussion(projectSections, notification),
      PROPOSAL_DISCUSSION_COMMENT: () => this.goToProposalDiscussion(projectSections, notification),
      PROPOSAL_ACCEPTED: () => this.goToProposalAccepted(notification),
      PROJECT_INVITATION: () => this.goToProjectInvitation(),
      PROJECT_UPDATED: () => this.goToProjectDashboard(notification),
      PROJECT_STATUS_UPDATED: () => this.goToProjectDashboard(notification),
      CAMPAIGN_STATUS_UPDATED: () =>
        this.navigateToCampaign(notification.object.workspaceId, notification.object.campaignId),
      ASSET_LIST_STATUS_UPDATED: () =>
        this.navigateToCampaign(notification.object.workspaceId, notification.object.campaignId),
      EVENT_CREATED: () => this.goToProjectCalendar(notification),
      EVENT_USER_ADDED: () => this.goToProjectCalendar(notification),
      EVENT_USER_REMOVED: () => this.goToProjectCalendar(notification, true),
      PROJECT_DATE: () => this.goToProjectDashboard(notification),
      CONVERSATION_CREATED: () =>
        this.goToProjectDashboardWithChatOpened(notification, {
          type: notification.object.conversationType.toLowerCase(),
          username: notification.object.sender,
          conversationId: notification.object.conversationId,
          ...(notification.object.channelUuid ? { channelUuid: notification.object.channelUuid } : {}),
          ...(notification.object.messageUuid ? { messageUuid: notification.object.messageUuid } : {}),
          ...(notification.object.parentMessageUuid
            ? { parentMessageUuid: notification.object.parentMessageUuid }
            : {}),
        }),
      CONVERSATION_MESSAGE_SENT: () =>
        this.goToProjectDashboardWithChatOpened(notification, {
          type: notification.object.conversationType.toLowerCase(),
          username: notification.object.sender,
          conversationId: notification.object.conversationId,
          ...(notification.object.channelUuid ? { channelUuid: notification.object.channelUuid } : {}),
          ...(notification.object.messageUuid ? { messageUuid: notification.object.messageUuid } : {}),
          ...(notification.object.parentMessageUuid
            ? { parentMessageUuid: notification.object.parentMessageUuid }
            : {}),
        }),
      CONVERSATION_USER_ADDED: () =>
        this.goToProjectDashboardWithChatOpened(notification, {
          type: notification.object.conversationType.toLowerCase(),
          username: notification.object.sender,
          conversationId: notification.object.conversationId,
          ...(notification.object.channelUuid ? { channelUuid: notification.object.channelUuid } : {}),
          ...(notification.object.messageUuid ? { messageUuid: notification.object.messageUuid } : {}),
          ...(notification.object.parentMessageUuid
            ? { parentMessageUuid: notification.object.parentMessageUuid }
            : {}),
        }),
      EXT_TASK_STATUS_UPDATED: () => void this.goToTaskPage(notification),
      EXT_TASK_USER_ADDED: () => void this.goToTaskPage(notification),
      EXT_TASK_USER_REMOVED: () => void this.goToTaskPage(notification),
      EXT_TASK_PRIORITY_UPDATED: () => void this.goToTaskPage(notification),
      EXT_TASK_DUE_DATE_UPDATED: () => void this.goToTaskPage(notification),
      EXT_TASK_SUBTASK_UPDATED: () => void this.goToTaskPage(notification),
      EXT_TASK_SUBTASK_USER_ADDED: () => void this.goToTaskPage(notification),
      EXT_TASK_SUBTASK_USER_REMOVED: () => void this.goToTaskPage(notification),
      EXT_TASK_DELETED: () => void this.goToTaskPage(notification),
      EXT_TASK_DESCRIPTION_UPDATED: () => void this.goToTaskPage(notification),
      EXT_TASK_DESCRIPTION_MENTION: () => void this.goToTaskPage(notification),
      EXT_TASK_PARTICIPANT_ADDED: () => void this.goToTaskPage(notification),
      EXT_TASK_PARTICIPANT_REMOVED: () => void this.goToTaskPage(notification),
      EXT_TASK_SUBTASK_PARTICIPANT_ADDED: () => void this.goToTaskPage(notification),
      EXT_TASK_SUBTASK_PARTICIPANT_REMOVED: () => void this.goToTaskPage(notification),
    };

    return handlerMap[notification.code] || null;
  }

  public navigateToBrand(): void {
    this.router.navigate(['/workspaces']);
  }

  public navigateToWorkspace(workspaceId: string): void {
    const wid: string = this.cryptoService.encryptString(workspaceId);
    this.router.navigate(['/workspaces', 'campaign-drive'], { queryParams: { wid } });
  }

  public navigateToCampaign(workspaceId: string, campaignId: string): void {
    const wid: string = this.cryptoService.encryptString(workspaceId);
    const dc: string = this.cryptoService.encryptString(campaignId);
    this.router.navigate(['/workspaces', 'campaigndrive', 'dashboard'], { queryParams: { wid, dc } });
  }

  /** Opens the page of a brand / workspace / campaign notification (no project). */
  public navigateToOrgEntity(obj: NotificationObject): void {
    const { workspaceId, campaignId }: OrgEntityIds = orgEntityIds(obj);

    if (obj.elementType === 'WORKSPACE' && workspaceId) {
      this.navigateToWorkspace(workspaceId);
      return;
    }
    if ((obj.elementType === 'CAMPAIGN' || !obj.elementType) && workspaceId && campaignId) {
      this.navigateToCampaign(workspaceId, campaignId);
      return;
    }
    this.navigateToBrand();
  }

  /** Entity the comment panel must be anchored on to show the notified message: the org id, file, page or element. */
  public getNotificationTargetId(obj: NotificationObject): string {
    const org: OrgEntityIds = orgEntityIds(obj);
    switch (obj.elementType) {
      case 'BRAND':
        return org.brandId || this.authService.getUserBrand()?.id || '';
      // an audience's notice shows on every brand: the active brand's feed opens it
      case 'GLOBAL':
        return this.authService.getUserBrand()?.id || '';
      case 'WORKSPACE':
        return org.workspaceId || '';
      case 'CAMPAIGN':
        return org.campaignId || '';
    }
    if (obj.fileId) return obj.fileId;
    if (obj.fileVotes) {
      const fileId: string | undefined = this.parseFileVotes(obj.fileVotes);
      if (fileId) return fileId;
    }
    if (obj.elementType === 'PAGE') return obj.filePageId || obj.elementUuid || '';
    return obj.elementUuid || '';
  }

  private goToSection(notification: NotificationItem, projectSections: ProjectSection[]): void {
    const projectId: string = notification?.projectId || notification?.object?.projectId;
    const sectionId: string =
      notification?.object?.sectionId ||
      projectSections.find((s: ProjectSection) => s.name === notification?.object?.sectionName)?.id ||
      '';
    if (projectId && sectionId) {
      this.router.navigate(['/projects', projectId, sectionId]);
    } else {
      this.goToProjectDashboard(notification);
    }
  }

  private navigateByIds(notification: NotificationItem): void {
    const obj: any = notification.object || {};
    const projectId: string = notification?.projectId || obj?.projectId;
    const sectionId: string = obj.sectionId;
    const subSectionId: string = obj.subSectionId;

    if (!projectId || !sectionId || !subSectionId) {
      this.goToProjectDashboard(notification);
      return;
    }

    const queryParams: Record<string, string | number | undefined> = this.buildSubSectionQueryParams(notification);
    const baseQueryParams: Record<string, string | number | undefined> = {
      ...queryParams,
      type: notification.category,
      notificationCode: notification.code,
      random: moment().unix(),
    };

    if (obj.fileId) {
      this.router.navigate(['projects', projectId, sectionId, subSectionId, 'preview'], {
        queryParams: { ...baseQueryParams, commentId: obj.commentId },
        fragment: obj.optionId || obj.typeId || obj.itemId,
      });
    } else if (obj.optionId && obj.versionNumber) {
      this.router.navigate(['projects', projectId, sectionId, subSectionId, 'files'], {
        queryParams: baseQueryParams,
      });
    } else if (obj.topicId) {
      this.router.navigate(['projects', projectId, sectionId, subSectionId, 'grid'], {
        queryParams: baseQueryParams,
      });
    } else {
      this.router.navigate(['projects', projectId, sectionId, subSectionId], {
        queryParams: baseQueryParams,
      });
    }
  }

  private enrichFromElementPath(notification: NotificationItem): void {
    const obj: any = notification.object || {};

    if (obj.elementType && obj.elementUuid) {
      if (obj.elementType === 'FOLDER' && !obj.topicId) {
        obj.topicId = obj.elementUuid;
      }
      if (obj.elementType === 'SUB-FOLDER' && !obj.optionId) {
        obj.optionId = obj.elementUuid;
      }
      if (obj.elementType === 'FILE' && !obj.fileId) {
        obj.fileId = obj.elementUuid;
      }
      if (obj.elementType === 'PAGE' && !obj.filePageId) {
        obj.filePageId = obj.elementUuid;
      }
    }

    if (!obj.elementPath) {
      return;
    }
    const path: Record<string, ElementPathNode> = normalizeElementPath(obj.elementPath);
    if (!obj.topicId && path['FOLDER']?.id) {
      obj.topicId = path['FOLDER'].id;
    }
    if (!obj.optionId && path['SUB-FOLDER']?.id) {
      obj.optionId = path['SUB-FOLDER'].id;
    }
  }

  /**
   * Drive-emitted notifications carry a file alias or directory uuid. The explorer
   * only reads its address from the shared view context, so mint one naming the
   * target and navigate with it. Deleted targets open their containing directory.
   */
  private async goToDriveTarget(notification: NotificationItem): Promise<void> {
    const obj: any = notification.object || {};
    const projectId: string = notification?.projectId || obj.projectId;
    const fileAliasUuid: string | undefined = notification.code === 'FILE_DELETED' ? undefined : obj.fileAliasUuid;
    const directoryUuid: string | undefined =
      notification.code === 'DIRECTORY_DELETED' ? obj.parentDirectoryUuid : obj.directoryUuid;

    if (!projectId || (!fileAliasUuid && !directoryUuid)) {
      this.goToProjectDashboard(notification);
      return;
    }
    await this.driveTargetNavigation.open(projectId, { directoryUuid, fileAliasUuid });
  }

  /** Drive comment notifications name the commented element and message but carry no Drive address, so build one. */
  private async goToDriveComment(notification: NotificationItem): Promise<void> {
    const obj: NotificationObject = notification.object ?? {};
    const projectId: string | undefined = notification?.projectId || obj.projectId;
    if (!projectId) {
      return;
    }
    await this.driveTargetNavigation.open(projectId, driveCommentTarget(obj));
  }

  public gotoNotificationSource(projectSections: ProjectSection[], notification: NotificationItem): void {
    this.enrichFromElementPath(notification);

    const obj: any = notification.object || {};
    if (typeof obj.path === 'string' && obj.path) {
      void this.router.navigateByUrl(obj.path);
      return;
    }
    if (obj.fileAliasUuid || obj.directoryUuid) {
      void this.goToDriveTarget(notification);
      return;
    }
    if (isDriveComment(obj)) {
      void this.goToDriveComment(notification);
      return;
    }

    const handler: (() => void) | null = this.getNotificationHandler(projectSections, notification);
    if (handler) {
      handler();
      return;
    }

    if (obj.sectionName && obj.subSectionName) {
      this.goToSubSection(projectSections, notification);
    } else if (obj.sectionId && obj.subSectionId) {
      this.navigateByIds(notification);
    } else if (obj.sectionName || obj.sectionId) {
      this.goToSection(notification, projectSections);
    } else {
      this.goToProjectDashboard(notification);
    }
  }
}
